import React, { useEffect, useRef, useState } from "react";

// Reusable closed-pill multi-select. The trigger button shows the current
// selection count (or first label / "All"); clicking it opens a panel with
// checkboxes + Select All / Clear All. Closes on outside click + Escape.
//
// Props:
//   label         e.g. "Profession" — shown on the closed button when no
//                 selection, and as the panel header.
//   options       string[] — choices to render.
//   selected      string[] — currently selected values.
//   onToggle      (event) => void — receives a synthetic checkbox event
//                 with { target: { value, checked } } so it can drop in
//                 to existing per-item change handlers.
//   onSelectAll   () => void
//   onClearAll    () => void
//   emptyText     string shown inside the panel when options.length === 0.
const MultiSelectDropdown = ({
  label,
  options = [],
  selected = [],
  onToggle,
  onSelectAll,
  onClearAll,
  emptyText = "No options available.",
}) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const summary = () => {
    if (selected.length === 0) return `All ${label.toLowerCase()}`;
    if (selected.length === 1) return selected[0];
    if (selected.length === options.length) return `All (${options.length})`;
    return `${selected.length} selected`;
  };

  return (
    <div className="" ref={wrapperRef}>
      <label className="block text-sm font-medium text-[var(--color-text-muted)]">
        {label}
      </label>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        aria-expanded={open}
        aria-haspopup="listbox"
        // Sizing mirrors the project's form-control baseline (index.css
        // :where(input...) rule) so this control lines up vertically with
        // the native <input> / <select> elements rendered alongside it
        // in the advanced-filter grid.
        className="mt-1 w-full flex items-center justify-between gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-2.5 py-1.5 min-h-[2.125rem] text-left text-[0.8125rem] leading-[1.45] text-[var(--color-text-strong)] hover:bg-[var(--color-surface-hover)] transition-colors"
      >
        <span className="truncate">{summary()}</span>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`w-4 h-4 shrink-0 transition-transform ${
            open ? "rotate-180" : ""
          }`}
          aria-hidden="true"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute z-30 mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] shadow-lg overflow-hidden"
        >
          <div className="max-h-56 overflow-y-auto p-2 space-y-1">
            {options.length === 0 ? (
              <div className="text-sm text-[var(--color-text-muted)] p-2">
                {emptyText}
              </div>
            ) : (
              options.map((opt) => (
                <label
                  key={opt}
                  className="flex items-center space-x-2 cursor-pointer hover:bg-[var(--color-surface-hover)] p-1 rounded"
                >
                  <input
                    type="checkbox"
                    value={opt}
                    checked={selected.includes(opt)}
                    onChange={onToggle}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm">{opt}</span>
                </label>
              ))
            )}
          </div>
          {options.length > 0 && (
            <div className="flex justify-between px-3 py-2 border-t border-[var(--color-border)] bg-[var(--color-surface-muted)]">
              <button
                type="button"
                onClick={onSelectAll}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                Select All
              </button>
              <button
                type="button"
                onClick={onClearAll}
                className="text-xs text-red-600 hover:text-red-800"
              >
                Clear All
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
